import type {
  AdminOrderPaymentCaptureDto,
  PaymentCaptureBlockedReason,
  PaymentCaptureFailureCode,
  PaymentCapturePhase,
} from '@carcheck/shared';

import { formatDateTime } from '../../utils/format';

/**
 * AUT-309 — turn the server's capture projection into sentences.
 *
 * Promoted here from `pages/orders/_lib/` by AUT-328, which added a second
 * consumer: the money-health tab lists live holds and must describe them in
 * exactly the words the order drawer uses for the same payment. The i18n keys
 * stay `orders.capture.story.*` on purpose — see `./index.ts`.
 *
 * ## The incident this exists for
 *
 * An order read «завершён», its payment read «холд» / «ещё не списано» /
 * «Выплата по заказу ещё не создана», and the owner concluded the money was
 * stuck with nobody working on it. He was wrong: a capture had been attempted
 * four minutes earlier, had failed on a transient `POST /api/login` timeout,
 * and the sweep had already claimed the row with five re-drives still budgeted.
 * The database knew all of it. The drawer rendered FIVE genuinely different
 * situations as one indistinguishable «холд / ещё не списано».
 *
 * `capture.phase` is exactly the distinction that was missing, and this file is
 * the only place that decides what each phase reads like:
 *
 *   - `not_applicable` — nothing to say. Renders NOTHING; a "нет активного
 *     холда" line under a captured payment is noise, not information.
 *   - `awaiting_trigger` / `in_flight` — one muted line. A hold doing its job.
 *   - `retry_scheduled` — a visible block that is **deliberately not an alert**.
 *     This is the exact state that was misread, and the fix is that it reads as
 *     "in hand", not as "broken".
 *   - `needs_operator` — the ONLY state that raises severity.
 *
 * ## Four facts the server owns; none is recomputed here
 *
 *  1. `attemptBudget` — read off the DTO. A hardcoded 6 would silently lie the
 *     day `STRANDED_REDRIVE_BUDGET` moves.
 *  2. `nextAttemptNotBefore` is a LOWER BOUND, not a schedule: the sweep ticks
 *     every five minutes and skips rows written to recently. A value in the past
 *     therefore means "on one of the next ticks" and must NEVER read as overdue
 *     — hence two separate sentences (`retry.nextAt` / `retry.imminent`) rather
 *     than one "не раньше {{time}}" that turns into an accusation once the
 *     clock passes it.
 *  3. Need-for-a-human is `blockedReason !== null` and nothing else. It is never
 *     inferred from a spent budget, a past deadline or a failure code.
 *  4. Presence invariants relied on: `nextAttemptNotBefore` is non-null iff
 *     `phase === 'retry_scheduled'`; `blockedReason` is non-null iff
 *     `phase === 'needs_operator'`. Both are still handled defensively — a
 *     missing value degrades one sentence, never the block.
 *
 * ## Why the vocabularies are `Record`s and the phase is a `switch`
 *
 * A tenth `blockedReason` shipping from the server must be a BUILD ERROR here,
 * not an empty row on the screen somebody is staring at during an incident.
 * `Record<Union, …>` gives that for the two vocabularies; the exhaustive
 * `switch` (no `default`, declared return type) gives it for the phase. This
 * mirrors the server's own `BLOCKED_REASON` table in
 * `capture-attempt-projection.ts`, which fails the same way for the same reason.
 */

/**
 * How loud the block is. Only `attention` is an alert — see the class comment.
 * `quiet` covers everything the automation is still handling, including
 * `retry_scheduled`.
 */
export type CaptureStoryTone = 'quiet' | 'attention';

/** One sentence, as an i18n key plus its interpolations. Rule #11: no copy here. */
export interface CaptureStoryText {
  key: string;
  values?: Readonly<Record<string, string | number>>;
}

export interface CaptureStory {
  phase: PaymentCapturePhase;
  tone: CaptureStoryTone;
  /** MUI `Alert` severity. `null` whenever `tone` is `quiet` — no alert at all. */
  severity: 'warning' | 'error' | null;
  /** The lead sentence. */
  headline: CaptureStoryText;
  /** Supporting sentences, in reading order. May be empty. */
  lines: CaptureStoryText[];
}

const KEY = 'orders.capture.story';

/**
 * Why the LAST attempt did not take. Three codes, three genuinely different
 * next actions — «не дошло до провайдера» and «провайдер отказал» are not the
 * same news, and before AUT-309 both read as silence.
 */
const FAILURE_KEY: Record<PaymentCaptureFailureCode, string> = {
  provider_unreachable: `${KEY}.failure.provider_unreachable`,
  provider_refused: `${KEY}.failure.provider_refused`,
  provider_answer_lost: `${KEY}.failure.provider_answer_lost`,
};

/**
 * Why nothing automatic will move this hold.
 *
 * Same nine-value vocabulary as the `[hold.sweep]` log lines, on purpose: an
 * operator quoting the screen and an engineer grepping the log must be talking
 * about the same row. The Russian stays close enough to each code that the
 * correspondence survives a support call.
 */
const BLOCKED_KEY: Record<PaymentCaptureBlockedReason, string> = {
  never_attempted_window_closing: `${KEY}.blocked.never_attempted_window_closing`,
  no_settlement_decision: `${KEY}.blocked.no_settlement_decision`,
  settlement_intent: `${KEY}.blocked.settlement_intent`,
  order_cancelled: `${KEY}.blocked.order_cancelled`,
  attempted_order_pre_terminal: `${KEY}.blocked.attempted_order_pre_terminal`,
  order_status_unexpected: `${KEY}.blocked.order_status_unexpected`,
  order_missing: `${KEY}.blocked.order_missing`,
  hold_window_closed: `${KEY}.blocked.hold_window_closed`,
  redrive_budget_exhausted: `${KEY}.blocked.redrive_budget_exhausted`,
};

/**
 * `order_missing` is the one reason that is not an operations problem: there is
 * an FK on `payments.order_id`, so a missing order is a data defect and belongs
 * to engineering. Everything else is a decision an operator can actually make,
 * and painting all nine red would flatten that difference back out again.
 */
const BLOCKED_SEVERITY: Record<PaymentCaptureBlockedReason, 'warning' | 'error'> = {
  never_attempted_window_closing: 'warning',
  no_settlement_decision: 'warning',
  settlement_intent: 'warning',
  order_cancelled: 'warning',
  attempted_order_pre_terminal: 'warning',
  order_status_unexpected: 'warning',
  order_missing: 'error',
  hold_window_closed: 'warning',
  redrive_budget_exhausted: 'warning',
};

/** «Последняя попытка: …» — omitted entirely on a hold nobody has claimed. */
const lastAttemptLine = (capture: AdminOrderPaymentCaptureDto): CaptureStoryText[] =>
  capture.lastAttemptAt === null
    ? []
    : [{ key: `${KEY}.lastAttempt`, values: { at: formatDateTime(capture.lastAttemptAt) } }];

/** The cause of the last attempt's failure, when the server recognised one. */
const failureLine = (capture: AdminOrderPaymentCaptureDto): CaptureStoryText[] =>
  capture.lastFailureCode === null ? [] : [{ key: FAILURE_KEY[capture.lastFailureCode] }];

/**
 * «Потрачено автоматических попыток: N из M». The budget comes off the DTO —
 * see fact 1 in the class comment. Suppressed at zero, where it says nothing.
 */
const attemptsLine = (capture: AdminOrderPaymentCaptureDto): CaptureStoryText[] =>
  capture.attemptsSpent === 0
    ? []
    : [
        {
          key: `${KEY}.attemptsSpent`,
          values: { spent: capture.attemptsSpent, budget: capture.attemptBudget },
        },
      ];

/**
 * The scheduled-retry headline.
 *
 * `attemptsSpent + 1` because the first, trigger-driven attempt is not counted
 * in the re-drive budget — the DTO says so explicitly, and «попытка 0 из 6»
 * would be nonsense to read.
 *
 * The past-bound branch is the whole point: a lower bound that has elapsed is
 * not a missed appointment, it is "any tick now", and saying «не раньше 14:00»
 * at 16:00 is precisely how a healthy row gets escalated by a human who thinks
 * it is late.
 */
const retryHeadline = (capture: AdminOrderPaymentCaptureDto, now: number): CaptureStoryText => {
  const attempt = capture.attemptsSpent + 1;
  const budget = capture.attemptBudget;
  const at = capture.nextAttemptNotBefore;
  if (at === null || Date.parse(at) <= now) {
    return { key: `${KEY}.retry.imminent`, values: { attempt, budget } };
  }
  return { key: `${KEY}.retry.nextAt`, values: { attempt, budget, at: formatDateTime(at) } };
};

/**
 * Build the block. `null` means render nothing at all.
 *
 * `now` is injectable so the "lower bound elapsed" branch is a unit test rather
 * than something that behaves differently at 09:00 and at 17:00.
 */
export const describeCaptureStory = (
  capture: AdminOrderPaymentCaptureDto,
  now: number = Date.now(),
): CaptureStory | null => {
  const { phase } = capture;
  switch (phase) {
    case 'not_applicable':
      // No live hold: single-stage, already captured, refunded, voided, failed.
      // The payment block above already says what happened to the money.
      return null;

    case 'awaiting_trigger':
      return {
        phase,
        tone: 'quiet',
        severity: null,
        headline: { key: `${KEY}.phase.awaiting_trigger` },
        lines: [],
      };

    case 'in_flight':
      return {
        phase,
        tone: 'quiet',
        severity: null,
        headline: { key: `${KEY}.phase.in_flight` },
        lines: [
          // A `settlement` claim moves a SLICE, not the whole hold. Reading an
          // in-flight partial as "списываем холд" mis-sizes the money.
          ...(capture.intent === 'settlement' ? [{ key: `${KEY}.intentSettlement` }] : []),
          ...attemptsLine(capture),
        ],
      };

    case 'retry_scheduled':
      return {
        phase,
        // NOT an alert. The regression this guards is the original misreading.
        tone: 'quiet',
        severity: null,
        headline: retryHeadline(capture, now),
        lines: [
          { key: `${KEY}.retry.reassurance` },
          ...failureLine(capture),
          ...lastAttemptLine(capture),
        ],
      };

    case 'needs_operator': {
      const reason = capture.blockedReason;
      return {
        phase,
        tone: 'attention',
        severity: reason === null ? 'warning' : BLOCKED_SEVERITY[reason],
        headline: { key: `${KEY}.needsOperator.title` },
        lines: [
          ...(reason === null ? [] : [{ key: BLOCKED_KEY[reason] }]),
          ...failureLine(capture),
          ...attemptsLine(capture),
          ...lastAttemptLine(capture),
        ],
      };
    }
  }
};
